// Bói Đê — La bàn phong thuỷ, hai lớp luận độc lập:
//
//  1. Bát Trạch  — theo CUNG PHI của người xem (năm sinh âm lịch + giới tính).
//                  Cố định cả đời, dùng để kê giường/bàn/hướng nhà.
//  2. Khí ngày   — theo NGŨ HÀNH CHI NGÀY. Đổi mỗi ngày, ai cũng như nhau,
//                  dùng để chọn hướng xuất hành/ngồi họp trong hôm đó.
//
// Hai lớp có thể nói khác nhau về cùng một hướng — đó là bình thường, nên trang
// nói rõ cách phối: lấy Bát Trạch làm gốc, khí ngày là lớp tinh chỉnh theo ngày.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { CalendarDays, Info } from "lucide-react";

import { BoideSettingsDialog } from "@/components/boide-settings-dialog";
import { LaBanBatTrach } from "@/components/la-ban-bat-trach";
import { LaBanKhiNgay } from "@/components/la-ban-khi-ngay";
import { IconLaBan, IconVanKhi } from "@/components/nav-icons";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useBoideUser } from "@/hooks/use-boide-user";
import {
  DU_NIEN_META,
  doiGioiTinh,
  luanTamHuong,
  namAmLichSinh,
  nhomMenh,
  tinhCungPhi,
} from "@/lib/bat-trach";
import { getVanKhiXemNgay } from "@/lib/boide-api";
import { MUC_KHI_META, luanHuongKhiNgay, nguHanhTuChuoi } from "@/lib/huong-khi-ngay";
import { cn } from "@/lib/utils";

type TabKey = "bat-trach" | "khi-ngay";

const TABS: { key: TabKey; label: string }[] = [
  { key: "bat-trach", label: "Bát Trạch" },
  { key: "khi-ngay", label: "Hướng theo khí" },
];

export const Route = createFileRoute("/_authenticated/tong-hop/la-ban")({
  validateSearch: (search: Record<string, unknown>): { tab?: TabKey } => ({
    tab: TABS.some((t) => t.key === search.tab) ? (search.tab as TabKey) : undefined,
  }),
  component: LaBanPage,
});

function LaBanPage() {
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const activeTab: TabKey = tab ?? "bat-trach";

  return (
    // main + overflow-y-auto: layout _authenticated không tự cuộn, mỗi trang phải
    // tự làm vùng cuộn của mình (giống diem-danh / hop-thu).
    <main className="app-feature-page app-feature-bg app-feature-bg--la-ban flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[760px] px-4 pb-[calc(6.5rem+env(safe-area-inset-bottom))] pt-5 sm:px-6 md:py-9">
        <header className="mb-5 flex items-center gap-3">
          <span className="grid size-12 shrink-0 place-items-center rounded-full border border-gold-soft/60 bg-card text-primary shadow-[0_8px_22px_rgba(83,55,18,0.08)]">
            <IconLaBan className="size-7" />
          </span>
          <div className="min-w-0">
            <h1 className="font-display text-[1.45rem] font-bold tracking-tight text-foreground sm:text-3xl">
              La bàn phong thuỷ
            </h1>
            <p className="mt-0.5 truncate text-xs text-muted-foreground sm:text-sm">
              Chọn hướng phù hợp cho việc lâu dài hoặc trong ngày
            </p>
          </div>
        </header>

        <div
          role="tablist"
          aria-label="Chọn lớp luận hướng"
          className="mb-5 grid grid-cols-2 gap-1 rounded-2xl bg-card p-1.5 shadow-[0_10px_28px_rgba(83,55,18,0.08)]"
        >
          {TABS.map((item) => {
            const active = item.key === activeTab;
            return (
              <button
                key={item.key}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() =>
                  navigate({ search: { tab: item.key === "bat-trach" ? undefined : item.key } })
                }
                className={cn(
                  "min-h-11 rounded-xl px-3 text-sm font-semibold transition active:scale-[0.98]",
                  active
                    ? "bg-primary text-primary-foreground shadow-[0_5px_14px_rgba(130,87,17,0.22)]"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {activeTab === "bat-trach" ? <BatTrachTab /> : <KhiNgayTab />}

        <p className="mt-5 flex items-start gap-2 px-2 text-[11px] leading-5 text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0 text-primary" />
          <span>
            Khi hai lớp nói khác nhau về một hướng: lấy Bát Trạch làm gốc cho việc lâu dài (kê
            giường, bàn làm việc, hướng nhà), còn khí ngày dùng để chọn hướng cho việc trong hôm đó
            (xuất hành, ngồi đàm phán). Nội dung mang tính tham khảo.
          </span>
        </p>
      </div>
    </main>
  );
}

// ─── Tab 1: Bát Trạch (theo cung phi) ────────────────────────────────────────

function BatTrachTab() {
  const { user, ready } = useBoideUser();
  const profile = user?.profile;
  const gioiTinh = doiGioiTinh(profile?.gender);
  const nam = namAmLichSinh(profile);

  if (!ready) return <Skeleton className="h-[520px] w-full rounded-3xl" />;

  if (!gioiTinh || !nam) {
    return (
      <section className="rounded-[1.5rem] bg-card p-6 shadow-[0_14px_36px_rgba(83,55,18,0.1)]">
        <span className="mb-4 grid size-11 place-items-center rounded-full border border-gold-soft/60 text-primary">
          <IconLaBan className="size-6" />
        </span>
        <h2 className="font-display text-base font-bold text-foreground">
          Cần ngày sinh và giới tính để lập cung phi
        </h2>
        <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
          Bát Trạch luận tám hướng theo cung phi, mà cung phi tính từ năm sinh âm lịch và giới tính.
          Bổ sung hồ sơ rồi quay lại là có ngay.
        </p>
        <BoideSettingsDialog
          trigger={
            <button
              type="button"
              className="mt-4 inline-flex rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground"
            >
              Cập nhật hồ sơ
            </button>
          }
        />
        <p className="mt-4 text-xs text-muted-foreground">
          Muốn xem hướng ngay mà chưa cần hồ sơ?{" "}
          <Link to="/tong-hop/la-ban" search={{ tab: "khi-ngay" }} className="text-primary">
            Xem hướng theo khí ngày
          </Link>
        </p>
      </section>
    );
  }

  const menh = tinhCungPhi(nam.nam, gioiTinh);
  const luan = luanTamHuong(menh);
  const tuCat = luan.filter((h) => h.tot);
  const tuHung = luan.filter((h) => !h.tot);

  return (
    <div className="space-y-4">
      <section className="relative overflow-hidden rounded-[1.65rem] bg-card p-4 shadow-[0_16px_42px_rgba(83,55,18,0.11)] sm:p-6">
        <span
          aria-hidden="true"
          className="absolute -right-16 -top-16 size-44 rounded-full border border-gold-soft/25"
        />
        <div className="relative flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-full border border-gold-soft/60 text-primary">
              <IconLaBan className="size-6" />
            </span>
            <div>
              <h2 className="font-display text-base font-bold text-foreground">Cung phi {menh}</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">{nhomMenh(menh)}</p>
            </div>
          </div>
          <span className="rounded-full border border-gold-soft/60 px-2.5 py-1 text-[11px] font-semibold text-primary">
            {gioiTinh === "nam" ? "Nam" : "Nữ"}, năm âm lịch {nam.nam}
          </span>
        </div>
        <p className="relative mt-3 text-xs leading-5 text-muted-foreground">
          Kim vàng ở đỉnh chỉ hướng bạn đang quay mặt tới; xoay điện thoại thì mặt la bàn xoay theo.
        </p>
        {!nam.chinhXac ? (
          <p className="relative mt-2 rounded-xl border border-gold-soft/50 px-3 py-2 text-[11px] leading-5 text-muted-foreground">
            Bạn sinh đầu năm dương lịch nên có thể vẫn thuộc năm âm lịch trước đó. Hãy bổ sung ngày
            sinh âm lịch trong hồ sơ để cung phi chuẩn hơn.
          </p>
        ) : null}
        <div className="relative mt-4">
          <LaBanBatTrach
            menh={menh}
            canCu={`${gioiTinh === "nam" ? "Nam" : "Nữ"}, năm âm lịch ${nam.nam}`}
          />
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-2">
        <DuNienList title="Tứ cát" tone="good" items={tuCat} />
        <DuNienList title="Tứ hung" tone="bad" items={tuHung} />
      </div>
    </div>
  );
}

function DuNienList({
  title,
  tone,
  items,
}: {
  title: string;
  tone: "good" | "bad";
  items: ReturnType<typeof luanTamHuong>;
}) {
  return (
    <section className="rounded-[1.5rem] bg-card p-4 shadow-[0_12px_32px_rgba(83,55,18,0.08)]">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "size-2.5 rounded-full",
            tone === "good" ? "bg-element-wood" : "bg-destructive",
          )}
        />
        <h3
          className={cn(
            "font-display text-base font-bold",
            tone === "good" ? "text-element-wood" : "text-destructive",
          )}
        >
          {title}
        </h3>
        <span className="ml-auto text-[11px] text-muted-foreground">{items.length} hướng</span>
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((h) => (
          <li key={h.ten} className="rounded-xl border border-border/60 px-3 py-2.5">
            <p className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-sm font-semibold text-foreground">{h.ten}</span>
              <span
                className={cn(
                  "text-xs font-bold",
                  tone === "good" ? "text-element-wood" : "text-destructive",
                )}
              >
                {h.duNien}
              </span>
              <span className="ml-auto rounded-full border border-border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                {DU_NIEN_META[h.duNien].muc}
              </span>
            </p>
            <p className="mt-1 text-[11px] leading-5 text-muted-foreground">{h.tomTat}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── Tab 2: Hướng theo khí ngày ──────────────────────────────────────────────

function KhiNgayTab() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [date, setDate] = useState(today);

  // Dùng chung endpoint Xem ngày: tất định, miễn phí, và đã trả sẵn ngũ hành
  // chi ngày + trực/sao/giờ hoàng đạo nên không phải tự suy ở FE.
  const query = useQuery({
    queryKey: ["van-khi-xem-ngay", date],
    queryFn: () => getVanKhiXemNgay(date),
    staleTime: 5 * 60 * 1000,
  });

  const result = query.data?.result;
  const khiNgay = nguHanhTuChuoi(result?.day_element);
  const luan = useMemo(() => (khiNgay ? luanHuongKhiNgay(khiNgay) : []), [khiNgay]);

  return (
    <div className="space-y-4">
      <section className="rounded-[1.5rem] bg-card p-4 shadow-[0_12px_32px_rgba(83,55,18,0.08)] sm:p-5">
        <div className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full border border-gold-soft/60 text-primary">
            <CalendarDays className="size-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-base font-bold text-foreground">
              Chọn ngày xem hướng
            </h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Khí và hướng phù hợp thay đổi theo từng ngày
            </p>
          </div>
          <Input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value || today)}
            className="h-10 w-[144px] shrink-0 rounded-xl border-gold-soft/60 bg-transparent px-2 text-xs sm:w-[168px]"
          />
        </div>
        {result ? (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
            <span>{result.lunar_display}</span>
            <span aria-hidden="true">·</span>
            <span>{result.can_chi_ngay}</span>
            <span aria-hidden="true">·</span>
            <span>Tiết {result.tiet_khi}</span>
            <span aria-hidden="true">·</span>
            <span className="font-semibold text-primary">Hành {result.day_element}</span>
          </div>
        ) : null}
      </section>

      {query.isLoading ? (
        <Skeleton className="h-[520px] w-full rounded-3xl" />
      ) : query.isError || !khiNgay ? (
        <p className="rounded-2xl bg-card p-5 text-sm text-muted-foreground shadow-[0_12px_30px_rgba(83,55,18,0.08)]">
          Chưa tra được khí ngày. Vui lòng thử lại sau.
        </p>
      ) : (
        <>
          <section className="relative overflow-hidden rounded-[1.65rem] bg-card p-4 shadow-[0_16px_42px_rgba(83,55,18,0.11)] sm:p-6">
            <span
              aria-hidden="true"
              className="absolute -right-16 -top-16 size-44 rounded-full border border-gold-soft/25"
            />
            <div className="relative flex items-start gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-full border border-gold-soft/60 text-primary">
                <IconVanKhi className="size-6" />
              </span>
              <div>
                <h2 className="font-display text-base font-bold text-foreground">
                  Hướng theo khí ngày
                </h2>
                <p className="mt-0.5 text-xs font-semibold text-primary">Khí ngày hành {khiNgay}</p>
              </div>
            </div>
            <p className="relative mt-3 text-xs leading-5 text-muted-foreground">
              Hướng sinh hoặc tỷ hòa với khí ngày là hợp; hướng tương khắc nên hạn chế. Dùng lớp
              luận này cho xuất hành, gặp gỡ và công việc trong ngày.
            </p>
            <div className="relative mt-4">
              <LaBanKhiNgay khiNgay={khiNgay} canCu={result ? result.can_chi_ngay : undefined} />
            </div>
          </section>

          <section className="rounded-[1.5rem] bg-card p-4 shadow-[0_12px_32px_rgba(83,55,18,0.08)]">
            <div className="flex items-end justify-between gap-3">
              <div>
                <h3 className="font-display text-base font-bold text-foreground">
                  Tám hướng hôm nay
                </h3>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Ưu tiên hướng Hợp, hạn chế hướng Kỵ
                </p>
              </div>
            </div>
            <ul className="mt-3 space-y-2">
              {luan.map((h) => {
                const meta = MUC_KHI_META[h.muc];
                return (
                  <li
                    key={h.ten}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-border/60 px-3 py-3 text-xs"
                  >
                    <span className="w-20 shrink-0 font-semibold text-foreground">{h.ten}</span>
                    <span className="w-10 shrink-0 text-muted-foreground">{h.hanh}</span>
                    <span
                      className={cn(
                        "ml-auto shrink-0 rounded-full border border-current/20 px-2 py-0.5 font-bold sm:order-last",
                        meta.mau,
                      )}
                    >
                      {meta.nhan}
                    </span>
                    <span className="order-last w-full min-w-0 text-muted-foreground sm:order-none sm:w-auto sm:flex-1 sm:truncate">
                      {h.ly_do}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          {result?.gio_hoang_dao?.length ? (
            <section className="rounded-[1.5rem] bg-card p-4 shadow-[0_12px_32px_rgba(83,55,18,0.08)]">
              <h3 className="font-display text-base font-bold text-foreground">
                Giờ hoàng đạo trong ngày
              </h3>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {result.gio_hoang_dao.map((gio) => (
                  <span
                    key={gio.gio_chi}
                    className="rounded-xl border border-gold-soft/60 px-3 py-2 text-center text-[11px] font-semibold text-primary"
                  >
                    {gio.gio_chi} {gio.khung_gio}
                  </span>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
